import { Box, Stack, Typography } from "@mui/material";
import { ErrorPanel, AlertBanner } from "@qubit/autoparts";
import { Trans, useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useFlag } from "~/config/flags";
import { formatLocalizedListParts } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { parseProductTempZone, TempZone } from "~/lib/tempZone";
import { selectLocale } from "~/redux/selectors/siteSelectors";

import { useWorkstationPickingConfiguration } from "./useWorkstationPickingConfiguration";

const TempZoneAlertBanner = () => {
  const { t } = useTranslation();
  const locale = useAppSelector(selectLocale);
  const trackedModalName = useMixpanelModalName("Start Picking");
  const { enableColorChanges } = useFlag();
  const {
    isEnabled,
    isError: isWorkstationPickingConfigurationError,
    isConfigurationReady,
    refetch: refetchWorkstationPickingConfiguration,
    configuration
  } = useWorkstationPickingConfiguration();

  if (!isEnabled) return null;

  if (isWorkstationPickingConfigurationError)
    return (
      <ErrorPanel
        sx={{
          px: 2,
          py: 1,
          mt: 2,
          borderWidth: "2px",
          borderRadius: "10px",
          "& > .MuiStack-root > .MuiStack-root": {
            fontSize: "1.2rem",
            alignItems: "center"
          },
          "& .MuiTypography-subtitle1": { fontSize: "1em" },
          "& .MuiSvgIcon-root": {
            fontSize: "1.42em",
            alignSelf: "center",
            transform: "translateY(0.08em)"
          },
          "& .MuiTypography-subtitle2": { fontSize: "1.1rem" }
        }}
        message={t("workstation picking configuration error")}
        tryAgain={{
          onTryAgain: async () => {
            mixpanelTrack({
              type: "Button Click",
              label: "Retry Picking Temp Zone Configuration",
              trackedPageName: trackedModalName
            });
            await refetchWorkstationPickingConfiguration();
          },
          text: t("retry")
        }}
      />
    );

  const isPlaceholder = !isConfigurationReady || !configuration;
  if (isPlaceholder && !enableColorChanges) return null;

  const parsedTemperatureZones = (configuration?.temperatureZones ?? [])
    .map(parseProductTempZone)
    .filter((zone): zone is TempZone => zone !== null);

  const tempZoneListParts =
    parsedTemperatureZones.length > 0
      ? formatLocalizedListParts(
          parsedTemperatureZones.map((tempZone) => t(tempZone)),
          locale
        )
      : [];

  const boldComponent = {
    bold: <Typography component="span" fontSize="inherit" fontWeight="bold" />
  };

  const banner = (
    <AlertBanner
      severity="info"
      sx={{
        px: 2,
        py: 0.5,
        mt: 2,
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: "12px",
        rowGap: 1,
        ".MuiAlert-icon": {
          gridColumn: 1,
          gridRow: 1,
          alignSelf: "center"
        },
        ".MuiAlert-message": {
          display: "contents"
        }
      }}
      collapseSx={{ width: "100%" }}
      skipCustomTypography
    >
      <Stack
        sx={{
          display: "contents"
        }}
      >
        <Typography
          fontSize="1.5rem"
          fontWeight="normal"
          aria-label="workstation temp zones message"
          sx={{
            gridColumn: 2,
            gridRow: 1
          }}
        >
          {!tempZoneListParts.length ? (
            <Trans
              i18nKey="workstation pickable all temp zones"
              components={boldComponent}
            />
          ) : (
            <Trans
              i18nKey="workstation pickable temp zones"
              components={{
                tempZones: (
                  <>
                    {tempZoneListParts.map((part, index) =>
                      part.type === "element" ? (
                        <span
                          style={{
                            fontWeight: "bold"
                          }}
                          key={`${part.type}-${index}`}
                        >
                          {part.value}
                        </span>
                      ) : (
                        <span key={`${part.type}-${index}`}>{part.value}</span>
                      )
                    )}
                  </>
                )
              }}
            />
          )}
        </Typography>
        <Typography
          fontSize="1.2rem"
          aria-label="temperature setting change instructions"
          sx={{
            gridColumn: 2,
            gridRow: 2
          }}
        >
          {t("workstation temperature change contact admin")}
        </Typography>
      </Stack>
    </AlertBanner>
  );

  if (isPlaceholder) {
    return (
      <Box aria-hidden width="100%" sx={{ visibility: "hidden" }}>
        {banner}
      </Box>
    );
  }

  return banner;
};

export default TempZoneAlertBanner;
